import React, { useCallback, useContext, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import _ from "lodash";

import { notImplementedError } from "utils/common";

import { EFieldStatus } from "entities/Field/constants";
import { IPackageTab } from "entities/Package/sdk";

const DueDiligenceFiltersContext = React.createContext({
  statusFilter: [] as Array<EFieldStatus>,
  toggleStatusFilter: notImplementedError as ({
    statuses,
  }: {
    statuses: Array<EFieldStatus>;
  }) => void,
  showOnlyFieldsWithTasks: false,
  toggleShowOnlyFieldsWithTasks: notImplementedError,
  showOnlyUpdatedFields: false,
  toggleShowOnlyUpdatedFields: notImplementedError,
  showOnlyFileFields: false,
  toggleShowOnlyFileFields: notImplementedError,
  showApprovalBoxes: true,
  toggleShowApprovalBoxes: notImplementedError,
  showOnlyCompletedFields: false,
  toggleShowOnlyCompletedFields: notImplementedError,
  showOnlyFieldsWithRemarks: false,
  toggleShowOnlyFieldsWithRemarks: notImplementedError,
  showOnlyFieldsWithUnreadMessages: false,
  toggleShowOnlyFieldsWithUnreadMessages: notImplementedError,
});

export interface IDueDiligenceFiltersContextProvider {
  children?: React.ReactNode;
}

export const DueDiligenceFiltersContextProvider = ({
  children,
}: IDueDiligenceFiltersContextProvider) => {
  const [searchParams] = useSearchParams();

  const [statusFilter, setStatusFilter] = useState<Array<EFieldStatus>>([]);
  const [showOnlyFieldsWithRemarks, setShowOnlyFieldsWithRemarks] =
    useState(false);
  const [showOnlyFieldsWithTasks, setShowOnlyFieldsWithTasks] = useState(false);
  const [showApprovalBoxes, setShowApprovalBoxes] = useState(true);
  const [showOnlyUpdatedFields, setShowOnlyUpdatedFields] = useState(
    () => searchParams.get("filtered_view") === "updated"
  );
  const [showOnlyFileFields, setShowOnlyFileFields] = useState(false);
  const [showOnlyCompletedFields, setShowOnlyCompletedFields] = useState(false);
  const [
    showOnlyFieldsWithUnreadMessages,
    setShowOnlyFieldsWithUnreadMessages,
  ] = useState(false);

  const toggleShowOnlyUpdatedFields = useCallback(() => {
    setShowOnlyUpdatedFields((prev) => !prev);
  }, []);

  const toggleShowOnlyFileFields = useCallback(() => {
    setShowOnlyFileFields((prev) => !prev);
  }, []);

  const toggleShowApprovalBoxes = useCallback(() => {
    setShowApprovalBoxes((prev) => !prev);
  }, []);

  const toggleShowOnlyCompletedFields = useCallback(() => {
    setShowOnlyCompletedFields((prevValue) => !prevValue);
  }, []);

  const toggleStatusFilter = useCallback(
    ({ statuses }: { statuses: Array<EFieldStatus> }) => {
      setStatusFilter((prevStatuses) => _.xor(prevStatuses, statuses));
    },
    []
  );

  const toggleShowOnlyFieldsWithRemarks = useCallback(() => {
    setShowOnlyFieldsWithRemarks((prev) => !prev);
  }, []);

  const toggleShowOnlyFieldsWithTasks = useCallback(() => {
    setShowOnlyFieldsWithTasks((prev) => !prev);
  }, []);

  const toggleShowOnlyFieldsWithUnreadMessages = useCallback(() => {
    setShowOnlyFieldsWithUnreadMessages((prev) => !prev);
  }, []);

  const contextValue = useMemo(
    () => ({
      showOnlyFieldsWithTasks,
      toggleShowOnlyFieldsWithTasks,
      showOnlyFieldsWithRemarks,
      toggleShowOnlyFieldsWithRemarks,
      statusFilter,
      toggleStatusFilter,
      showOnlyUpdatedFields,
      toggleShowOnlyUpdatedFields,
      showOnlyFileFields,
      toggleShowOnlyFileFields,
      showApprovalBoxes,
      toggleShowApprovalBoxes,
      showOnlyCompletedFields,
      toggleShowOnlyCompletedFields,
      showOnlyFieldsWithUnreadMessages,
      toggleShowOnlyFieldsWithUnreadMessages,
    }),
    [
      showOnlyFieldsWithTasks,
      toggleShowOnlyFieldsWithTasks,
      showOnlyFieldsWithRemarks,
      toggleShowOnlyFieldsWithRemarks,
      statusFilter,
      toggleStatusFilter,
      showOnlyUpdatedFields,
      toggleShowOnlyUpdatedFields,
      showOnlyFileFields,
      toggleShowOnlyFileFields,
      showApprovalBoxes,
      toggleShowApprovalBoxes,
      showOnlyCompletedFields,
      toggleShowOnlyCompletedFields,
      showOnlyFieldsWithUnreadMessages,
      toggleShowOnlyFieldsWithUnreadMessages,
    ]
  );

  return (
    <DueDiligenceFiltersContext.Provider value={contextValue}>
      {children}
    </DueDiligenceFiltersContext.Provider>
  );
};

export const useDueDiligenceFilters = ({
  selectedTab,
}: {
  selectedTab?: IPackageTab;
} = {}) => {
  const {
    showOnlyFieldsWithTasks,
    toggleShowOnlyFieldsWithTasks,
    showOnlyFieldsWithRemarks,
    toggleShowOnlyFieldsWithRemarks,
    statusFilter,
    toggleStatusFilter,
    showOnlyUpdatedFields,
    toggleShowOnlyUpdatedFields,
    showOnlyFileFields,
    toggleShowOnlyFileFields,
    showApprovalBoxes,
    toggleShowApprovalBoxes,
    showOnlyCompletedFields,
    toggleShowOnlyCompletedFields,
    showOnlyFieldsWithUnreadMessages,
    toggleShowOnlyFieldsWithUnreadMessages,
  } = useContext(DueDiligenceFiltersContext);

  return {
    showOnlyFieldsWithTasks,
    toggleShowOnlyFieldsWithTasks,
    showOnlyFieldsWithRemarks,
    toggleShowOnlyFieldsWithRemarks,
    statusFilter,
    toggleStatusFilter,
    showOnlyUpdatedFields,
    toggleShowOnlyUpdatedFields,
    showOnlyFileFields,
    toggleShowOnlyFileFields,
    showApprovalBoxes,
    toggleShowApprovalBoxes,
    showOnlyCompletedFields,
    toggleShowOnlyCompletedFields,
    showOnlyFieldsWithUnreadMessages,
    toggleShowOnlyFieldsWithUnreadMessages,
  };
};
